import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

/**
 * VideoPlayer component for Google Drive embedded videos
 * Handles playback, tracking, and progress reporting
 */
const VideoPlayer = ({ courseId, dealId, videoId, videoUrl, onProgressUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({
    position: 0,
    percentage: 0,
    completed: false
  });
  
  const iframeRef = useRef(null);
  const progressInterval = useRef(null);
  
  // Load initial progress
  useEffect(() => {
    const fetchProgress = async () => {
      try {
        // Skip progress fetching if no dealId (viewing as admin/non-student)
        if (!dealId) {
          setLoading(false);
          return;
        }
        
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Authentication required');
          return;
        }
        
        const config = {
          headers: {
            'x-auth-token': token
          }
        };
        
        const response = await axios.get(
          `/api/video-courses/${courseId}/progress/${dealId}`,
          config
        );
        
        // Find progress for this specific video
        const videoProgress = response.data.find(p => p.video_id === videoId);
        
        if (videoProgress) {
          setProgress({
            position: videoProgress.current_position,
            percentage: videoProgress.completion_percentage,
            completed: videoProgress.is_completed
          });
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching video progress:', err);
        setLoading(false);
      }
    };
    
    fetchProgress();
    
    // Clean up interval on unmount
    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [courseId, dealId, videoId]);
  
  // Update progress periodically
  useEffect(() => {
    if (!loading && !error && dealId) { // Only track progress if we have a dealId
      // Start progress tracking interval
      progressInterval.current = setInterval(async () => {
        try {
          // Here we would ideally get the current time from the iframe player
          // This is a placeholder - actual implementation would depend on
          // the specific video player and API available
          const currentTime = Math.min(progress.position + 5, 100); // Placeholder
          const currentPercentage = Math.min(progress.percentage + 1, 100); // Placeholder
          const isCompleted = currentPercentage >= 95; // Mark as completed at 95%
          
          // Update local state
          setProgress({
            position: currentTime,
            percentage: currentPercentage,
            completed: isCompleted
          });
          
          // Call the callback if provided
          if (onProgressUpdate) {
            onProgressUpdate({
              position: currentTime,
              percentage: currentPercentage,
              completed: isCompleted
            });
          }
          
          // Only send progress updates if we have a dealId (student view)
          if (dealId) {
            // Send progress update to server
            const token = localStorage.getItem('token');
            if (!token) return;
            
            const config = {
              headers: {
                'Content-Type': 'application/json',
                'x-auth-token': token
              }
            };
            
            await axios.post(
              `/api/video-courses/${courseId}/progress`,
              {
                dealId,
                videoId,
                position: currentTime,
                percentage: currentPercentage,
                completed: isCompleted
              },
              config
            );
          }
        } catch (err) {
          console.error('Error updating video progress:', err);
        }
      }, 30000); // Update every 30 seconds
    }
    
    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [loading, error, courseId, dealId, videoId, progress, onProgressUpdate]);
  
  // Extract Google Drive file ID from URL
  const getGoogleDriveEmbedUrl = (url) => {
    try {
      // Handle different Google Drive URL formats
      let fileId = '';
      
      if (url.includes('drive.google.com/file/d/')) {
        // Format: https://drive.google.com/file/d/FILE_ID/view
        fileId = url.split('/file/d/')[1].split('/')[0];
      } else if (url.includes('drive.google.com/open?id=')) {
        // Format: https://drive.google.com/open?id=FILE_ID
        fileId = url.split('open?id=')[1].split('&')[0];
      } else if (url.includes('docs.google.com/document/d/')) {
        // Format: https://docs.google.com/document/d/FILE_ID/edit
        fileId = url.split('/document/d/')[1].split('/')[0];
      } else {
        // Assume the URL is already the file ID
        fileId = url;
      }
      
      return `https://drive.google.com/file/d/${fileId}/preview`;
    } catch (error) {
      console.error('Error parsing Google Drive URL:', error);
      return url; // Return the original URL if parsing fails
    }
  };
  
  if (loading) {
    return <div className="flex justify-center items-center h-64 bg-gray-100 rounded-lg">Loading video...</div>;
  }
  
  if (error) {
    return <div className="flex justify-center items-center h-64 bg-red-100 text-red-700 rounded-lg">{error}</div>;
  }
  
  const embedUrl = getGoogleDriveEmbedUrl(videoUrl);
  
  return (
    <div className="video-player rounded-lg overflow-hidden shadow-lg">
      <div className="relative" style={{ paddingTop: '56.25%' /* 16:9 Aspect Ratio */ }}>
        <iframe
          ref={iframeRef}
          title="Video Player"
          className="absolute top-0 left-0 w-full h-full"
          src={embedUrl}
          frameBorder="0"
          allowFullScreen
        ></iframe>
      </div>
      
      {/* Video progress bar */}
      <div className="mt-2 px-2">
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className="bg-blue-600 h-2.5 rounded-full" 
            style={{ width: `${progress.percentage}%` }}
          ></div>
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{progress.completed ? 'Completed' : `${progress.percentage}% complete`}</span>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;